import React from "react";
import { useTranslation } from "react-i18next";
import "./NewValuesSection.css";

/**
 * UniTech Engineering — A propos, section 06 "Valeurs et engagements".
 * Conserve l'export et le nom du composant ValuesSection deja utilise dans
 * Nous_Sommes.jsx. Aucun fichier i18n global n'est modifie.
 */
const CONTENT = {
  fr: {
    eyebrow: "NOS VALEURS, DANS LES FAITS",
    titleFirst: "Quatre engagements.",
    titleAccent: "Une même exigence.",
    values: [
      {
        key: "innovation",
        title: "Innovation",
        description: "Explorer des approches nouvelles pour répondre à des besoins réels."
      },
      {
        key: "simplicity",
        title: "Simplicité",
        description: "Rendre la technologie avancée plus facile à intégrer et à utiliser."
      },
      {
        key: "quality",
        title: "Qualité",
        description: "Tester, documenter et améliorer les solutions avec rigueur."
      },
      {
        key: "collaboration",
        title: "Collaboration",
        description: "Construire avec les clients et partenaires, à l’écoute du terrain."
      }
    ]
  },
  en: {
    eyebrow: "OUR VALUES, PUT INTO PRACTICE",
    titleFirst: "Four commitments.",
    titleAccent: "One shared standard.",
    values: [
      {
        key: "innovation",
        title: "Innovation",
        description: "Explore new approaches to address real-world needs."
      },
      {
        key: "simplicity",
        title: "Simplicity",
        description: "Make advanced technology easier to integrate and use."
      },
      {
        key: "quality",
        title: "Quality",
        description: "Test, document and improve our solutions with care."
      },
      {
        key: "collaboration",
        title: "Collaboration",
        description: "Work with customers and partners, listening to the realities on the ground."
      }
    ]
  }
};

function ValueIcon({ kind }) {
  const common = {
    viewBox: "0 0 32 32",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    focusable: "false",
    "aria-hidden": "true"
  };

  if (kind === "innovation") {
    return (
      <svg {...common}>
        <path d="M16 4.5 19.3 12.7 27.5 16l-8.2 3.3L16 27.5l-3.3-8.2L4.5 16l8.2-3.3L16 4.5Z" />
        <path d="M25 4.5v4.5M22.75 6.75h4.5" strokeWidth="1.5" />
      </svg>
    );
  }

  if (kind === "simplicity") {
    return (
      <svg {...common}>
        <path d="M8 24 24 8M14 8h10v10" strokeWidth="2.2" />
      </svg>
    );
  }

  if (kind === "quality") {
    return (
      <svg {...common}>
        <path d="m7 16 6 6L25 9" strokeWidth="2.3" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <circle cx="16" cy="16" r="11" />
      <circle cx="16" cy="16" r="5.7" />
      <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function ValuesSection() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = CONTENT[language] || CONTENT.fr;

  return (
    <section
      id="about-values"
      className="about-values"
      aria-labelledby="about-values-heading"
    >
      <div className="about-values__container">
        <header className="about-values__header">
          <p className="about-values__eyebrow">{content.eyebrow}</p>
          <h2 id="about-values-heading" className="about-values__heading">
            <span>{content.titleFirst}</span>
            <span className="about-values__headingAccent">{content.titleAccent}</span>
          </h2>
        </header>

        <div className="about-values__grid">
          {content.values.map((value) => (
            <article
              key={value.key}
              className={`about-values__card about-values__card--${value.key}`}
            >
              <div className="about-values__icon" aria-hidden="true">
                <ValueIcon kind={value.key} />
              </div>
              <h3 className="about-values__cardTitle">{value.title}</h3>
              <p className="about-values__cardDescription">{value.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
